import { z } from 'zod';
import {
  businessDateSchema,
  dataFreshnessSchema,
  envelope,
  instantSchema,
  snapshotEnvelope,
  uuidSchema,
} from './common.ts';

/** Operational endpoints: filters, attention, data quality, system status (docs/API_DESIGN.md §9, §11). */

export const filtersSchema = z.strictObject({
  salespeople: z.array(
    z.strictObject({ id: uuidSchema, fullName: z.string(), isActive: z.boolean() }),
  ),
  sources: z.array(z.strictObject({ id: uuidSchema, code: z.string(), name: z.string() })),
  pipelines: z.array(z.strictObject({ id: uuidSchema, name: z.string() })),
  containerTypes: z.array(
    z.strictObject({ id: uuidSchema, code: z.string(), name: z.string(), isAuto: z.boolean() }),
  ),
});
export const filtersResponseSchema = snapshotEnvelope(filtersSchema);

export const attentionSchema = z.strictObject({
  overdueFollowUps: z.strictObject({
    total: z.number().int(),
    bySalesperson: z.array(
      z.strictObject({
        salespersonId: uuidSchema.nullable(),
        displayName: z.string().nullable(),
        count: z.number().int(),
      }),
    ),
    byAge: z.strictObject({
      lt1d: z.number().int(),
      d1to3: z.number().int(),
      gt3d: z.number().int(),
    }),
  }),
  uncontactedLeads: z.strictObject({
    count: z.number().int(),
    thresholdMinutes: z.number().int(),
  }),
  /** Missed visits need visit records, which the connected CRM does not provide (stage-based visits). */
  missedVisits: z.strictObject({ available: z.literal(false) }),
  /** Counts for the business date of `asOf`. */
  dataQuality: z.strictObject({
    date: businessDateSchema,
    leadsWithoutSource: z.number().int(),
    wonWithoutItems: z.number().int(),
  }),
});
export const attentionResponseSchema = snapshotEnvelope(attentionSchema);

const lineItemCountSchema = z.strictObject({
  lineItems: z.number().int(),
  containers: z.number().int(),
});

export const dataQualitySchema = z.strictObject({
  leadsWithoutSource: z.number().int(),
  wonWithoutItems: z.number().int(),
  wonWithoutCustomer: z.number().int(),
  lostWithoutReason: z.number().int(),
  /** A state (open leads in an unmapped stage now), not a period figure. */
  unmappedStage: z.number().int(),
  unknownContainerType: lineItemCountSchema,
  autoContainerType: lineItemCountSchema,
});
export const dataQualityResponseSchema = envelope(dataQualitySchema);

export const systemStatusSchema = z.strictObject({
  datasetKind: z.enum(['demo', 'live']).nullable(),
  dataSource: z.strictObject({ kind: z.string(), name: z.string() }).nullable(),
  freshness: dataFreshnessSchema.extend({
    entities: z.array(
      z.strictObject({
        entityType: z.string(),
        lastSuccessAt: instantSchema.nullable(),
        lastAttemptAt: instantSchema.nullable(),
        lastError: z.string().nullable(),
        consecutiveFailures: z.number().int(),
        status: z.enum(['fresh', 'delayed', 'stale']),
      }),
    ),
  }),
  /** The worker heartbeat is not persisted yet (PLANNED). */
  worker: z.null(),
  lastSyncRuns: z.array(
    z.strictObject({
      kind: z.string(),
      status: z.enum(['running', 'succeeded', 'failed']),
      startedAt: instantSchema,
      finishedAt: instantSchema.nullable(),
      stats: z.record(z.string(), z.unknown()),
      error: z.string().nullable(),
    }),
  ),
});
export const systemStatusResponseSchema = snapshotEnvelope(systemStatusSchema);
